/**
 * Project/Yard Selector Component
 * Dropdown selector for projects and yards to be placed in the AppBar
 * Styling: no border, 18px text size, bold, persistent across all pages
 */

import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { colorPalette, PROJECT_SELECTOR } from '../../theme';

const ProjectYardSelector = ({
  projects = [],
  selectedProjectId = null,
  onProjectSelect,
  displayEmpty = true,
  placeholder = 'Projects',
}) => {
  const handleChange = (event) => {
    const projectId = event.target.value;
    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  // ASS-84 item 2: a Draft project's page must show its name in the top
  // bar. This selector independently filtered to 'active' only -- even
  // after projectSlice.js's fetch fix makes draft projects reach
  // projectList, MUI's <Select> renders blank whenever `value` doesn't
  // match any of its MenuItem options, which is exactly what happened
  // here for a draft project's own page. Matches ProjectListPage.jsx's
  // rule: active and draft are shown; inactive stays hidden.
  const visibleProjects = projects.filter(p => p.status === 'active' || p.status === 'draft');

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: PROJECT_SELECTOR.minWidth,
        m: 0,  // No margin to align left
        '& .MuiOutlinedInput-root': {
          height: '40px',
          width: 'fit-content',
          border: PROJECT_SELECTOR.borderStyle,
          '& fieldset': {
            border: PROJECT_SELECTOR.borderStyle,
          },
          '&:hover fieldset': {
            border: PROJECT_SELECTOR.borderStyle,
          },
          '&.Mui-focused fieldset': {
            border: PROJECT_SELECTOR.borderStyle,
          },
        },
        '& .MuiSelect-select': {
          fontSize: `${PROJECT_SELECTOR.fontSize}px`,
          fontWeight: PROJECT_SELECTOR.fontWeight,
          fontStyle: PROJECT_SELECTOR.fontStyle,
          padding: '8px 32px 8px 12px',
          color: colorPalette.quillFeather,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiSvgIcon-root': {
          color: colorPalette.quillFeather,
        },
      }}
    >
      <Select
        value={selectedProjectId || ''}
        onChange={handleChange}
        displayEmpty={displayEmpty}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: colorPalette.backgroundColor,
              border: `1px solid ${colorPalette.lightSteel}`,
              maxHeight: 400,
            },
          },
        }}
      >
        {displayEmpty && (
          <MenuItem value="">
            <Box component="span" className="project-selector-text" sx={{ fontSize: '18px' }}>
              {placeholder}
            </Box>
          </MenuItem>
        )}
        {visibleProjects.map((project) => (
          <MenuItem key={project.id} value={project.id}>
            <Box component="span" className="project-selector-text" sx={{ fontSize: '18px' }}>
              {project.name} {project.type === 'yard' ? '(Yard)' : ''}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ProjectYardSelector;
